import { useMemo, type ReactElement, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { formatCell as defaultFormatCell } from '../format-cell';
import { titleCase, useResourceUI, type CrudOp, type ResourceUITheme } from '../context';
import type { SchemaFieldMeta } from '../introspect';

type Row = Record<string, unknown>;

/** Per-column cell override (matches `formatCell`'s return type for tanstack v8). */
export type CellRenderer = (value: unknown, row: Row) => ReactElement | string;

type TableClassNames = NonNullable<ResourceUITheme['table']>;

const DEFAULTS: Required<TableClassNames> = {
  root: 'w-full',
  container: 'overflow-x-auto rounded-md border border-border',
  table: 'w-full text-xs sm:text-sm',
  thead: 'bg-muted text-left',
  th: 'px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-foreground whitespace-nowrap',
  td: 'px-2 py-1.5 sm:px-3 sm:py-2',
  row: 'border-t border-border',
  rowSelected: 'bg-accent',
  pager:
    'mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-muted-foreground',
  pageBtn: 'rounded border border-border px-3 py-1 hover:bg-muted disabled:opacity-40',
};

export type ResourceTableProps = {
  model: string;
  rows: Row[];
  isLoading: boolean;
  error?: unknown;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pageIndex: number;
  pageSize: number;
  onPageIndexChange: (i: number) => void;
  exclude?: readonly string[];
  /** Explicit column order (and allowlist): when set, only these fields show,
   *  in this order. Names not present/visible are skipped. */
  columnOrder?: readonly string[];
  columnRenderers?: Partial<Record<string, CellRenderer>>;
  /** Extra columns that are NOT model fields (e.g. a derived/related value). Appended
   *  after the schema columns, before the actions column. Not sortable. */
  extraColumns?: Array<{ id: string; header?: string; cell: (row: Row) => ReactNode }>;
  onRowClick?: (row: Row) => void;
  /** Optional per-row extra className (e.g. to highlight error rows). */
  rowClassName?: (row: Row) => string;
  selectedRowId?: string;
  actions?: (row: Row, ctx: { can: (op: CrudOp) => boolean }) => ReactNode;
  /** Passed to the configured `can(model, op, auth, row)` policy hook. */
  currentUser?: Row | null;
  /** Per-instance cell formatter override (default: bundled `formatCell`). */
  formatCell?: (value: unknown, type: string) => ReactElement | string;
  /** Per-instance class overrides (merged over the provider theme). */
  classNames?: TableClassNames;
};

/**
 * Schema-driven table for any ZenStack model. Reads schema, theme, i18n texts,
 * labels and policy from `<ResourceUIProvider>`; pass per-instance overrides via
 * props. Pair with a query hook that owns rows + sorting + pagination.
 */
export function ResourceTable({
  model,
  rows,
  isLoading,
  error,
  sorting,
  onSortingChange,
  pageIndex,
  pageSize,
  onPageIndexChange,
  exclude = [],
  columnOrder,
  columnRenderers,
  extraColumns,
  onRowClick,
  rowClassName,
  selectedRowId,
  actions,
  currentUser,
  formatCell,
  classNames,
}: ResourceTableProps) {
  const ui = useResourceUI();
  const cn = { ...DEFAULTS, ...ui.theme?.table, ...classNames };
  const label = (f: string) => (ui.label ? ui.label(model, f) : titleCase(f));
  const fmt = formatCell ?? defaultFormatCell;
  const tx = ui.texts?.table ?? {};
  const canAction = (op: CrudOp, row: Row) =>
    ui.can ? ui.can(model, op, currentUser ?? null, row) : true;

  const fields = useMemo(() => {
    const all = (ui.schema.models[model]?.fields ?? {}) as Record<string, SchemaFieldMeta>;
    const excluded = new Set(exclude);
    let visible = Object.values(all).filter((f) => !f.omit && !excluded.has(f.name));
    if (rows.length > 0) {
      const present = new Set(Object.keys(rows[0]!));
      visible = visible.filter((f) => present.has(f.name));
    }
    if (columnOrder && columnOrder.length) {
      const byName = new Map(visible.map((f) => [f.name, f]));
      visible = columnOrder.map((n) => byName.get(n)).filter(Boolean) as typeof visible;
    }
    return visible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.schema, model, exclude, columnOrder, rows]);

  const columns = useMemo<ColumnDef<Row>[]>(() => {
    const columnHelper = createColumnHelper<Row>();
    const cols: ColumnDef<Row>[] = fields.map((f) =>
      columnHelper.accessor(f.name, {
        header: label(f.name),
        enableSorting: true,
        cell: (ctx) => {
          const override = columnRenderers?.[f.name];
          if (override) return override(ctx.getValue(), ctx.row.original);
          return fmt(ctx.getValue(), f.type);
        },
      }),
    );
    for (const ec of extraColumns ?? []) {
      cols.push(
        columnHelper.display({
          id: ec.id,
          header: ec.header ?? '',
          enableSorting: false,
          cell: (ctx) => <>{ec.cell(ctx.row.original)}</>,
        }),
      );
    }
    if (actions) {
      cols.push(
        columnHelper.display({
          id: '__actions',
          header: '',
          enableSorting: false,
          cell: (ctx) => {
            const row = ctx.row.original;
            return <>{actions(row, { can: (op) => canAction(op, row) })}</>;
          },
        }),
      );
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, actions, columnRenderers, extraColumns]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange,
    manualSorting: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (error)
    return (
      <div className="text-sm text-destructive">
        {tx.error ?? 'Error'}: {(error as Error).message}
      </div>
    );

  return (
    <div className={cn.root}>
      <div className={cn.container}>
        <table className={cn.table}>
          <thead className={cn.thead}>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  return (
                    <th
                      key={h.id}
                      onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                      className={`${cn.th} ${sortable ? 'cursor-pointer select-none' : ''}`}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {{ asc: ' ↑', desc: ' ↓' }[h.column.getIsSorted() as string] ?? null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground">
                  {tx.loading ?? 'Loading…'}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground">
                  {tx.empty ?? 'No rows.'}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((r) => {
                const id = (r.original as Row).id as string | undefined;
                const isSelected = !!selectedRowId && id === selectedRowId;
                return (
                  <tr
                    key={r.id}
                    onClick={onRowClick ? () => onRowClick(r.original) : undefined}
                    className={[
                      cn.row,
                      onRowClick ? 'cursor-pointer' : '',
                      isSelected ? cn.rowSelected : 'hover:bg-muted',
                      rowClassName?.(r.original as Row) ?? '',
                    ].join(' ')}
                  >
                    {r.getVisibleCells().map((c) => (
                      <td key={c.id} className={cn.td}>
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={cn.pager}>
        <div>
          {tx.page ?? 'Page'} <span className="font-medium text-foreground">{pageIndex + 1}</span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={pageIndex === 0 || isLoading}
            onClick={() => onPageIndexChange(Math.max(0, pageIndex - 1))}
            className={cn.pageBtn}
          >
            {tx.prev ?? 'Prev'}
          </button>
          <button
            type="button"
            disabled={rows.length < pageSize || isLoading}
            onClick={() => onPageIndexChange(pageIndex + 1)}
            className={cn.pageBtn}
          >
            {tx.next ?? 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
